// The one router. Hash-based so it works identically in the browser, from file://, inside the
// Tauri webview and in a published single-file build. Routes are declared by the module registry.
// Screen changes use the View Transitions API when the engine supports it (and motion is allowed):
// the old screen dissolves while the new one rises — no layout jumps, no library.
import { useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';

export interface Route { path: string; parts: string[]; query: URLSearchParams }

function parse(): Route {
  const raw = (location.hash || '#/now').slice(1);
  const [path = '/now', q = ''] = raw.split('?');
  return { path, parts: path.split('/').filter(Boolean).map(decodeURIComponent), query: new URLSearchParams(q) };
}
let current = parse();
const listeners = new Set<() => void>();

type ViewTransition = { ready?: Promise<unknown>; finished?: Promise<unknown>; updateCallbackDone?: Promise<unknown> };
type VTDoc = Document & { startViewTransition?: (cb: () => void) => ViewTransition | undefined };
const quiet = () => undefined;
const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

function commit(next: Route) {
  const screenChanged = next.parts[0] !== current.parts[0] || next.parts[1] !== current.parts[1];
  const apply = () => { current = next; listeners.forEach((l) => l()); };
  const d = document as VTDoc;
  if (screenChanged && d.startViewTransition && !reduced() && !document.hidden) {
    const vt = d.startViewTransition(() => { flushSync(apply); window.scrollTo({ top: 0 }); });
    // a second navigation during the dissolve aborts the first one (Safari reports it as an unhandled AbortError)
    vt?.ready?.catch(quiet); vt?.finished?.catch(quiet); vt?.updateCallbackDone?.catch(quiet);
  } else {
    apply();
    if (screenChanged) window.scrollTo({ top: 0 });
  }
}

const sync = () => { const next = parse(); if (next.path !== current.path || next.query.toString() !== current.query.toString()) commit(next); };
window.addEventListener('hashchange', sync);
window.addEventListener('popstate', sync);

export function navigate(path: string, opts: { replace?: boolean } = {}) {
  const target = '#' + (path.startsWith('/') ? path : '/' + path);
  if (opts.replace) history.replaceState(null, '', target); else if (location.hash !== target) history.pushState(null, '', target);
  commit(parse());
}
export function useRoute(): Route {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => current);
}
export const personPath = (id: string, tab?: string) => `/people/${encodeURIComponent(id)}${tab ? '/' + tab : ''}`;
